import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import FrontdeskTabs from "@components/layout/FrontdeskTabs";
import { canAccess, hasScheduling, isOwner } from "@utils/access";
import { useClinicScope } from "@context/ClinicContext";
import { transferAllowedFor } from "@utils/access";
import Spinner from "@components/ui/Spinner";
import ReactivationList from "@components/scheduling/ReactivationList";
import ReactivationOverviewTab from "@components/scheduling/analytics/ReactivationOverviewTab";
import ReactivationCampaignsTab from "@components/scheduling/ReactivationCampaignsTab";
import ReactivationQueueTab from "@components/scheduling/ReactivationQueueTab";
import ReactivationSettingsTab from "@components/scheduling/ReactivationSettingsTab";
import StartCampaignModal from "@components/scheduling/StartCampaignModal";

const TABS = [
  ["overview", "Overview"],
  ["list", "Call list"],
  ["campaigns", "Campaigns"],
  ["queue", "Up next"],
  ["settings", "Settings"],
];

/**
 * Whether a clinic has a scheduling system behind it. The backend's own
 * verdict wins; older rows only name a provider, so fall back to that.
 */
function schedulingIntegrated(c) {
  return c.has_scheduling ?? Boolean(c.active_pms_provider);
}

/**
 * Which clinics an admin may pick here: one with a PMS books, one with
 * outbound calls and no PMS runs transfer campaigns.
 */
function reactivationEligible(c) {
  return schedulingIntegrated(c) || Boolean(c.outbound_calls_enabled);
}

/**
 * Patient reactivation: what the calls are doing, who to call, and the
 * campaigns themselves. It lands on the Overview. The clinic comes from the
 * shared scope (an admin picks it in the tab row; a clinic user has none),
 * so every tab and the start dialog follow the same clinic.
 *
 * The page is part of the frontdesk (voice) surface: its tab row shows
 * Reactivation lit and the other tabs go back to /voice. The clinical
 * All Patients / Form Library row is never shown here.
 */
export default function ReactivationPage() {
  const navigate = useNavigate();
  const [chosenTab, setTab] = useState("overview");
  const { clinicId, clinics, loading: clinicsLoading, isAdmin } = useClinicScope();
  // The call list is read from the PMS, so a clinic without one has no list
  // to show (the backend 400s it) and its tab is left out. A clinic user's
  // verdict came with sign-in; an admin's comes from the clinic they picked.
  const clinicHasScheduling = useMemo(() => {
    if (!isAdmin) return hasScheduling();
    const row = clinics.find((c) => c.id === clinicId);
    return row ? schedulingIntegrated(row) : false;
  }, [isAdmin, clinics, clinicId]);
  const tabs = clinicHasScheduling ? TABS : TABS.filter(([key]) => key !== "list");
  // Same shape for the transfer permission: the clinic's, not the viewer's.
  const transferAllowed = useMemo(
    () => transferAllowedFor({ isAdmin, clinics, clinicId }), [isAdmin, clinics, clinicId]);
  // An admin can switch from a PMS clinic to one without while on the call
  // list; the tab is gone, so the page falls back to the overview.
  const tab = chosenTab === "list" && !clinicHasScheduling ? "overview" : chosenTab;
  // What the start dialog is for: the rows the user ticked, or a random draw
  // from the list on screen, capped by how many rows that list could offer.
  // null keeps it closed.
  const [pendingStart, setPendingStart] = useState(null);
  const canFrontdesk = useMemo(() => isOwner() || canAccess("frontdesk"), []);

  const handleStarted = (campaign, { keepOpen = false } = {}) => {
    if (!keepOpen) setPendingStart(null);
    setTab("campaigns");
  };

  const goFrontdesk = (key) => {
    if (key !== "reactivation") navigate("/voice", { state: { frontdeskTab: key } });
  };

  return (
    <div className="flex flex-col h-screen">
      {/* No clinical tab row here: this page belongs to the frontdesk
          surface, whose own tab row below is the way around. The account
          menu still links back to the rest of the app. */}
      <Navbar showTabs={false} showFieldSearch={false} />
      {canFrontdesk && (
        <FrontdeskTabs
          active="reactivation"
          onSelect={goFrontdesk}
          clinicFilter={reactivationEligible}
        />
      )}
      <main className="flex-1 overflow-auto p-6">
        <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* An admin's clinic list is still on its way: rendering the tabs now
            would fetch for no clinic, then again for the real one. */}
        {clinicsLoading && <div className="flex justify-center py-10"><Spinner /></div>}
        {!clinicsLoading && tab === "overview" && <ReactivationOverviewTab clinicId={clinicId} />}
        {!clinicsLoading && tab === "list" && (
          <ReactivationList
            selectedClinicId={clinicId}
            onStartCampaign={(patients) => setPendingStart({ mode: "manual", patients })}
            onStartRandomCampaign={(eligibleCount, source) =>
              setPendingStart({ mode: "random", eligibleCount,
                                list: source?.key, listTitle: source?.title })}
          />
        )}
        {!clinicsLoading && tab === "campaigns" && <ReactivationCampaignsTab clinicId={clinicId} />}
        {!clinicsLoading && tab === "queue" && <ReactivationQueueTab clinicId={clinicId} />}
        {!clinicsLoading && tab === "settings" && <ReactivationSettingsTab clinicId={clinicId} transferAllowed={transferAllowed} />}

        <StartCampaignModal
          show={Boolean(pendingStart)}
          mode={pendingStart?.mode || "manual"}
          patients={pendingStart?.patients || []}
          eligibleCount={pendingStart?.eligibleCount || 0}
          list={pendingStart?.list}
          listTitle={pendingStart?.listTitle}
          clinicId={clinicId}
          hasScheduling={clinicHasScheduling}
          transferAllowed={transferAllowed}
          onClose={() => setPendingStart(null)}
          onStarted={handleStarted}
        />
      </main>
    </div>
  );
}
